import { truncateWithEllipsis } from "../../app/helpers/helper";
import LeftArrowIcon from "../../assets/icons/LeftArrowIcon";
import RightArrowIcon from "../../assets/icons/RightArrowIcon";
import {
  SupportReportIssueAddIcon,
  SupportStatusInProgressIcon,
  SupportStatusResolvedIcon,
  SupportTransactionCopyIcon,
} from "../../assets/icons/SupportIcons";
import Loader from "../../components/common/Loader";
import {
  SupportTicket,
  extractTickets,
  getApiErrorMessage,
  getRelativeTime,
  getStatusLabel,
  isApiSuccess,
  normalizeSupportStatus,
} from "./supportUtils";
import gluedin from "gluedin-shorts-js";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const supportModule = new gluedin.GluedInSupportModule();

const Support = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const defaultLanguage = localStorage.getItem("defaultLanguage");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const resolveLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await supportModule.getTicketList({
        limit: 30,
        offset: 1,
      });
      const parsedTickets = extractTickets(response);
      setTickets(parsedTickets);
      if (!isApiSuccess(response) && parsedTickets.length === 0) {
        setErrorMessage(
          getApiErrorMessage(
            response,
            resolveLabel(
              "label-support-list-error",
              "Unable to fetch support issues right now.",
            ),
          ),
        );
      }
    } catch (error: any) {
      setTickets([]);
      setErrorMessage(
        getApiErrorMessage(
          error?.response || error,
          resolveLabel(
            "label-support-list-error",
            "Unable to fetch support issues right now.",
          ),
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const copyToClipboard = async (value: string) => {
    if (!value) return;
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "true");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  return (
    <div className="full-sec"><div className="right-sec profile-content-list support-page">
        <div className="full-box support-full-box">
          <div className="back-header support-header">
            <ul className="list list-inline">
              <li className="back-btn">
                <button
                  className="back-btn-a"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  {defaultLanguage === "en" ? (
                    <LeftArrowIcon />
                  ) : (
                    <RightArrowIcon />
                  )}
                  {t("back-btn")}
                </button>
              </li>
              <li className="common-heading">
                {resolveLabel("label-support", "Support")}
              </li>
            </ul>
          </div>

          {isLoading && <Loader />}

          {!isLoading && (
            <div className="support-content">
              <h3 className="support-content__title">
                {resolveLabel("label-your-issues", "Your Issues")}

                <button
                  type="button"
                  className="support-report-btn"
                  onClick={() => navigate("/support/report")}
                >
                  <SupportReportIssueAddIcon />

                  {resolveLabel("label-report-issue", "Report an Issue")}
                </button>
              </h3>

              {errorMessage && (
                <div className="support-error-block">
                  <p>{errorMessage}</p>
                  <button
                    type="button"
                    className="support-error-block__retry"
                    onClick={fetchTickets}
                  >
                    {resolveLabel("label-retry", "Retry")}
                  </button>
                </div>
              )}

              {!errorMessage && tickets.length === 0 && (
                <div className="support-empty-card">
                  <p>
                    {resolveLabel(
                      "label-no-support-issues",
                      "No support issues yet.",
                    )}
                  </p>
                </div>
              )}

              {tickets.map((ticket) => {
                const status = normalizeSupportStatus(ticket.status);
                const statusLabel = getStatusLabel(status);
                const statusClass =
                  status === "RESOLVED"
                    ? "support-status-chip--resolved"
                    : status === "CLOSED"
                    ? "support-status-chip--closed"
                    : "support-status-chip--open";

                return (
                  <article
                    key={ticket.id}
                    className="support-ticket-card"
                    onClick={() => navigate(`/support/issue/${ticket.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(`/support/issue/${ticket.id}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="support-ticket-card__top">
                      <div className="flex flex-col gap-2">
                        <div className="support-ticket-card__transaction">
                          {resolveLabel(
                            "label-transaction-id",
                            "Transaction ID:",
                          )}{" "}
                          {ticket.transactionId
                            ? truncateWithEllipsis(ticket.transactionId, 12)
                            : "-"}
                          {ticket.transactionId && (
                            <button
                              type="button"
                              className="support-ticket-card__copy"
                              onClick={(event) => {
                                event.stopPropagation();
                                copyToClipboard(ticket.transactionId);
                              }}
                              aria-label={resolveLabel(
                                "label-copy-transaction-id",
                                "Copy transaction ID",
                              )}
                            >
                              <SupportTransactionCopyIcon />
                            </button>
                          )}
                        </div>

                        <h4 className="support-ticket-card__title">
                          {ticket.title}
                        </h4>

                        <div className="support-ticket-card__meta">
                          <span>
                            {getRelativeTime(
                              ticket.updatedAt || ticket.createdAt,
                              t,
                            )}
                          </span>
                          {ticket.hasResponse && (
                            <span className="support-ticket-card__response">
                              <i className="fa fa-circle" aria-hidden="true" />
                              {resolveLabel(
                                "label-received-response",
                                "Received Response",
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <span className={`support-status-chip ${statusClass}`}>
                        {status === "RESOLVED" && (
                          <SupportStatusResolvedIcon />
                        )}
                        {(status === "OPEN" || status === "IN_PROGRESS") && (
                          <SupportStatusInProgressIcon />
                        )}
                        {status === "CLOSED" && (
                          <i className="fa fa-ban" aria-hidden="true" />
                        )}
                        {statusLabel}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;
